/** Portal URL helpers: mutate query without changing pathname semantics for Accounting Hub SPA shell. */

export type PortalSearchPatchMode = 'push' | 'replace';

export type PortalSearchPatch = Record<string, string | null | undefined>;

export type PortalSearchSyncFn = (
  updates: PortalSearchPatch,
  mode?: PortalSearchPatchMode,
) => void;

/** Strip optional leading "?". */
const rawSearchWithoutQuestion = (search: string): string =>
  search.startsWith('?') ? search.slice(1) : search;

/** Parse current location search string into mutable URLSearchParams. */
export const parsePortalSearchParams = (search: string): URLSearchParams =>
  new URLSearchParams(search === '' || search === '?' ? '' : rawSearchWithoutQuestion(search));

/** Apply search param updates: null/undefined/empty string removes the key. */
export const buildPathWithUpdatedSearch = (
  pathname: string,
  search: string,
  updates: PortalSearchPatch,
): string => {
  const normalizedPath =
    pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  const params = parsePortalSearchParams(search);
  for (const [key, value] of Object.entries(updates)) {
    if (value === undefined || value === null || value === '') {
      params.delete(key);
    } else {
      params.set(key, value);
    }
  }
  const qs = params.toString();
  return qs ? `${normalizedPath}?${qs}` : normalizedPath;
};

export const portalLocationEquals = (aPath: string, aSearch: string, bPath: string, bSearch: string): boolean => {
  const na = aPath.length > 1 && aPath.endsWith('/') ? aPath.slice(0, -1) : aPath;
  const nb = bPath.length > 1 && bPath.endsWith('/') ? bPath.slice(0, -1) : bPath;
  const sa = aSearch.startsWith('?') ? aSearch : aSearch === '' ? '' : `?${rawSearchWithoutQuestion(aSearch)}`;
  const sb = bSearch.startsWith('?') ? bSearch : bSearch === '' ? '' : `?${rawSearchWithoutQuestion(bSearch)}`;
  return na === nb && sa === sb;
};

export function pickAllowedParam<T extends string>(
  raw: string | null,
  allowed: readonly T[],
  fallback: T,
): T {
  if (raw !== null && allowed.includes(raw as T)) {
    return raw as T;
  }
  return fallback;
}

/** Parses positive integer page index; clamps to range [fallback, max]. */
export const parsePositiveIntParam = (
  raw: string | null,
  fallback: number,
  opts?: { max?: number },
): number => {
  if (raw === null || raw === '') {
    return fallback;
  }
  const v = Number.parseInt(raw, 10);
  if (!Number.isInteger(v) || v < 1) {
    return fallback;
  }
  const max = opts?.max ?? 9999;
  return Math.min(v, max);
};
